import { useState, useMemo } from "react";
import {
    BarChart,
    Bar,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    LabelList,
} from "recharts";

import "./styles.css";

const UsageMetricsCharts = ({
    usageMetrics = [],
    yearMonth,
    monthLabel,
    monthTotals = {},
    isClosed = false,
}) => {
    const metricOptions = [
        { key: "inputTokens", label: "Input Tokens", formatter: (v) => v },
        { key: "outputTokens", label: "Output Tokens", formatter: (v) => v },
        { key: "inputCost", label: "Input Cost (USD)", formatter: (v) => `$${v.toFixed(4)}` },
        { key: "outputCost", label: "Output Cost (USD)", formatter: (v) => `$${v.toFixed(4)}` },
        { key: "totalCost", label: "Total Cost (USD)", formatter: (v) => `$${v.toFixed(4)}` },
    ];

    const [selectedMetric, setSelectedMetric] = useState(metricOptions[0]);

    const isCostMetric = selectedMetric.key.toLowerCase().includes("cost");

    const formatMoney = (value) =>
        new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(Number(value || 0));

    const formatTokens = (value) => {
        const v = Number(value || 0);
        if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)}M`;
        if (v >= 1_000) return `${(v / 1_000).toFixed(2)}k`;
        return `${v}`;
    };

    const formatCompact = (value) => {
        if (value == null || isNaN(value)) return "";
        const abs = Math.abs(value);
        if (isCostMetric) {
            if (abs === 0) return "$0";
            if (abs >= 1000) return `$${(value / 1000).toFixed(1)}k`;
            if (abs >= 1) return `$${value.toFixed(2)}`;
            return `$${value.toFixed(3)}`;
        }
        if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
        if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
        return `${value}`;
    };

    const formatBarLabel = (value) => {
        if (!value) return "";
        const abs = Math.abs(value);
        if (isCostMetric) {
            if (abs >= 1000) return `${(value / 1000).toFixed(1)}k`;
            if (abs >= 1) return value.toFixed(2);
            if (abs >= 0.01) return value.toFixed(2);
            return value.toFixed(3);
        }
        if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
        if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
        return `${value}`;
    };

    const chartData = useMemo(() => {
        const pad = (n) => n.toString().padStart(2, "0");

        let targetYear;
        let targetMonth; // zero-based

        if (yearMonth && /^\d{4}-\d{2}$/.test(yearMonth)) {
            const [y, m] = yearMonth.split("-").map(Number);
            targetYear = y;
            targetMonth = m - 1;
        } else {
            const today = new Date();
            targetYear = today.getFullYear();
            targetMonth = today.getMonth();
        }

        const daysInMonth = new Date(targetYear, targetMonth + 1, 0).getDate();

        const aggregated = (usageMetrics || []).reduce((acc, curr) => {
            if (!curr || !curr.date) return acc;
            const [y, m] = curr.date.split("-").map(Number);
            if (y !== targetYear || m - 1 !== targetMonth) return acc;

            const key = curr.date;
            if (!acc[key]) acc[key] = { ...curr };
            else acc[key][selectedMetric.key] = (acc[key][selectedMetric.key] || 0) + (curr[selectedMetric.key] || 0);
            return acc;
        }, {});

        return Array.from({ length: daysInMonth }, (_, i) => {
            const day = i + 1;
            const dateStr = `${targetYear}-${pad(targetMonth + 1)}-${pad(day)}`;
            const rec = aggregated[dateStr];
            return {
                date: dateStr,
                value: rec ? Number(rec[selectedMetric.key] || 0) : 0,
                dayLabel: day.toString(),
            };
        });
    }, [usageMetrics, selectedMetric, yearMonth]);

    const insights = useMemo(() => {
        const totalCostSeries = (usageMetrics || [])
            .filter((m) => m && m.date)
            .reduce((acc, curr) => {
                const key = curr.date;
                acc[key] = (acc[key] || 0) + Number(curr.totalCost || 0);
                return acc;
            }, {});

        const entries = Object.entries(totalCostSeries).filter(([, v]) => v > 0);
        const activeDays = entries.length;
        const total = Number(monthTotals?.totalCost || 0);
        const avgPerActiveDay = activeDays ? total / activeDays : 0;

        let peakDay = null;
        let peakValue = 0;
        entries.forEach(([date, value]) => {
            if (value > peakValue) {
                peakValue = value;
                peakDay = date;
            }
        });

        const peakDayLabel = peakDay
            ? Number(peakDay.split("-")[2])
            : null;

        return { activeDays, avgPerActiveDay, peakDayLabel, peakValue };
    }, [usageMetrics, monthTotals]);


    return (
        <div className="usage-metrics-charts">
            <div className="chart-summary">
                <div className="chart-summary-top">
                    <div className="chart-summary-headline">
                        <span className="chart-summary-eyebrow">Total spent in {monthLabel || yearMonth || ""}</span>
                        <div className="chart-summary-value-row">
                            <span className="chart-summary-value">{formatMoney(monthTotals?.totalCost)}</span>
                            <span className={`chart-summary-badge ${isClosed ? "closed" : "ongoing"}`}>
                                {isClosed ? "Closed" : "In progress"}
                            </span>
                        </div>
                    </div>
                    <select
                        className="chart-summary-select"
                        value={selectedMetric.key}
                        onChange={(e) =>
                            setSelectedMetric(
                                metricOptions.find((o) => o.key === e.target.value) || metricOptions[0]
                            )
                        }
                    >
                        {metricOptions.map((o) => (
                            <option key={o.key} value={o.key}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="chart-summary-stats">
                    <div className="chart-stat">
                        <span className="chart-stat-label">Input cost</span>
                        <span className="chart-stat-value">{formatMoney(monthTotals?.inputCost)}</span>
                    </div>
                    <div className="chart-stat">
                        <span className="chart-stat-label">Output cost</span>
                        <span className="chart-stat-value">{formatMoney(monthTotals?.outputCost)}</span>
                    </div>
                    <div className="chart-stat">
                        <span className="chart-stat-label">Input tokens</span>
                        <span className="chart-stat-value">{formatTokens(monthTotals?.inputTokens)}</span>
                    </div>
                    <div className="chart-stat">
                        <span className="chart-stat-label">Output tokens</span>
                        <span className="chart-stat-value">{formatTokens(monthTotals?.outputTokens)}</span>
                    </div>
                    <div className="chart-stat">
                        <span className="chart-stat-label">Active days</span>
                        <span className="chart-stat-value">{insights.activeDays}</span>
                    </div>
                    <div className="chart-stat">
                        <span className="chart-stat-label">Avg / active day</span>
                        <span className="chart-stat-value">{formatMoney(insights.avgPerActiveDay)}</span>
                    </div>
                    <div className="chart-stat">
                        <span className="chart-stat-label">Peak day</span>
                        <span className="chart-stat-value">
                            {insights.peakDayLabel
                                ? `Day ${insights.peakDayLabel} · ${formatMoney(insights.peakValue)}`
                                : "—"}
                        </span>
                    </div>
                </div>
            </div>

            {chartData.length === 0 || chartData.every((d) => !d.value) ? (
                <div className="chart-empty">No usage data for this month yet.</div>
            ) : (
                <div className="chart-scroll-wrapper">
                    <ResponsiveContainer width="100%" height={340}>
                        <BarChart
                            data={chartData}
                            margin={{ top: 28, right: 16, bottom: 8, left: 8 }}
                            barCategoryGap={4}
                        >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-lighter-gray)" />
                            <XAxis
                                dataKey="dayLabel"
                                interval={0}
                                tick={{ fontSize: 11, fill: "#666" }}
                                tickLine={false}
                                axisLine={{ stroke: "var(--color-lighter-gray)" }}
                            />
                            <YAxis
                                width={56}
                                tick={{ fontSize: 11, fill: "#666" }}
                                tickLine={false}
                                axisLine={false}
                                tickFormatter={formatCompact}
                            />
                            <Tooltip
                                cursor={{ fill: "rgba(0,0,0,0.04)" }}
                                contentStyle={{
                                    borderRadius: 8,
                                    border: "1px solid var(--color-lighter-gray)",
                                    fontSize: 12,
                                }}
                                formatter={(value) => [selectedMetric.formatter(value), selectedMetric.label]}
                                labelFormatter={(label) => `Day ${label}`}
                            />
                            <Bar
                                dataKey="value"
                                fill="var(--color-accent)"
                                radius={[4, 4, 0, 0]}
                                maxBarSize={32}
                            >
                                <LabelList
                                    dataKey="value"
                                    position="top"
                                    content={({ value, x, y, width }) => {
                                        if (!value) return null;
                                        const label = formatBarLabel(value);
                                        return (
                                            <text
                                                x={x + width / 2}
                                                y={y - 6}
                                                fill="#333"
                                                textAnchor="middle"
                                                fontSize={11}
                                                fontWeight={700}
                                            >
                                                {label}
                                            </text>
                                        );
                                    }}
                                />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default UsageMetricsCharts;
